import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { useAppContext } from 'providers/AppProvider';
import { TooltipComponent, TitleComponent, LegendComponent, ToolboxComponent } from 'echarts/components';
import { PieChart } from 'echarts/charts';
import { CanvasRenderer } from 'echarts/renderers';
import { rgbaColor } from 'helpers/utils';
import { useState, useEffect, useRef } from 'react';
import { usePowerStationFilterStore } from '../stores/PowerStationFilterStore';
import { ConditionType, ConditionTypeLabel } from '../model/enumerations/condition-type.model';

echarts.use([TooltipComponent, TitleComponent, LegendComponent, ToolboxComponent, PieChart, CanvasRenderer]);

const ThermalAnomaliesChart = () => {
	const { getThemeColor } = useAppContext();
	const chartRef = useRef<any>(null);

	const [showChart, setShowChart] = useState(false);

	const activeAnomalyRecords = usePowerStationFilterStore((state) => state.activeAnomalyRecords);
	const conditionFilter = usePowerStationFilterStore((state) => state.conditionFilter);
	const filterActiveAnomalyRecordsByCondition = usePowerStationFilterStore((state) => state.filterActiveAnomalyRecordsByCondition);

	// Dados da série
	const seriesData = Object.values(ConditionType).reverse().map((condition, index) => ({
		key: condition,
		value: activeAnomalyRecords.filter(x => x.condition === condition).length,
		name: ConditionTypeLabel[condition]
	}));

	const getDefaultOptions = (getThemeColor: (name: string) => string) => {
		// Build legend selected object based on seriesData values
		const selected: Record<string, boolean> = {};
		seriesData.forEach(item => {
			selected[item.name] = item.value > 0;
		});

		let total = activeAnomalyRecords.length > 0 ? activeAnomalyRecords.length : 1;

		return {
			color: [
				'rgba(255, 0, 0, 0.7)',
				'rgba(255, 170, 0, 0.8)',
				'rgba(255, 255, 0, 0.5)',
				'rgba(0, 255, 0, 0.3)'
			],
			legend: {
				data: Object.values(ConditionType)
					.filter(condition => condition != ConditionType.NORMAL)
					.reverse()
					.map(key => ConditionTypeLabel[key]),
				bottom: '0px',
				left: '0px',
				orient: 'vertical',
				align: 'left',
				textStyle: {
					color: getThemeColor('gray-800'),
					fontSize: 12
				},
				selected: selected
			},
			toolbox: {
				show: true,
				feature: {
					restore: {
						show: conditionFilter.length > 0,
						title: 'Limpar filtro'
					},
				}
			},
			series: [
				{
					type: 'pie',
					radius: '50%',
					center: ['50%', '40%'],
					label: {
						formatter: (params: { value: number; }) => {
							return ((params.value / total) * 100).toFixed(1) + '%';
						},
						color: getThemeColor('gray-700')
					},
					data: seriesData,
					emphasis: {
						itemStyle: {
							shadowBlur: 10,
							shadowOffsetX: 0,
							shadowColor: rgbaColor(getThemeColor('gray-600'), 0.5)
						}
					}
				}
			],
			tooltip: {
				trigger: 'item',
				padding: [7, 10],
				backgroundColor: getThemeColor('gray-100'),
				borderColor: getThemeColor('gray-300'),
				textStyle: { color: getThemeColor('gray-1100') },
				borderWidth: 1,
				transitionDuration: 0,
				axisPointer: {
					type: 'none'
				}
			}
		}
	};

	useEffect(() => {
		setShowChart(true);
	}, []);

	useEffect(() => {
		if (showChart && chartRef.current) {
			chartRef.current.getEchartsInstance().resize();
		}
	}, [showChart, activeAnomalyRecords]);

	const handleClick = (params: any) => {
		filterActiveAnomalyRecordsByCondition([params.data.key]);
	}

	const handleLegendClick = (params: any) => {
		const selectedMap = params.selected;
		const activeLegends = Object.entries(selectedMap)
			.filter(([_, value]) => value === true)
			.map(([key]) => Object.keys(ConditionTypeLabel).find(condition => ConditionTypeLabel[condition] === key));

		filterActiveAnomalyRecordsByCondition(activeLegends.filter((legend): legend is string => legend !== undefined));
	};

	const handleRestore = (params: any) => {
		filterActiveAnomalyRecordsByCondition([]);
	}

	return (
		<>
			{showChart && (
				<ReactEChartsCore
					key={activeAnomalyRecords.length}
					ref={chartRef}
					echarts={echarts}
					option={getDefaultOptions(getThemeColor)}
					style={{ minHeight: '350px', width: '100%' }}
					onEvents={{
						click: handleClick,
						legendselectchanged: handleLegendClick,
						restore: handleRestore
					}}
				/>
			)}
		</>
	);
};

export default ThermalAnomaliesChart;
